// src/components/files/EditeurAnnotation.tsx — SCE Issues
// Annoter une photo (09/10) : flèche, cercle, trait libre, texte, trois
// couleurs, retour en arrière. Maquette validée :
// https://claude.ai/artifact/DcEcrXUbuMHMLCAfPxV5TJ
//
// Deux usages :
//  - « publier » : depuis la visionneuse, sur une photo déjà envoyée. Le
//    résultat part dans l'activité, comme un nouveau commentaire ;
//  - « envoi » : sur une photo pas encore envoyée (déclaration, commentaire).
//    La version annotée partira avec l'original.
// L'original n'est jamais modifié : on dessine sur une COPIE, exportée en
// JPEG (2 400 px au plus sur le grand côté, assez pour zoomer, raisonnable
// en 4G).
//
// Outil « Déplacer » (retours du 09/10) : on saisit une forme du doigt pour la
// replacer. Choisie, elle porte deux poignées : au coin, pour l'agrandir ou la
// réduire ; au-dessus, pour la faire pivoter (aimantée aux angles droits). Elle
// change aussi de couleur d'un toucher, ou se supprime. « Annuler le dernier
// geste » défait tout cela.
//
// Texte : TT Norms Pro, la police de l'application, sans contour ; taille
// réglable (A− / A+) avant de le poser, ou ensuite une fois choisi.
//
// Sur PC : la photo à gauche sur toute la hauteur, les outils dans un panneau
// à droite (comme la visionneuse).
//
// Affiché par un portail, hors de la page : l'éditeur s'ouvre parfois dans le
// formulaire de déclaration, et valider un texte l'envoyait (page rechargée).
//
// PDF (12/10) : les mêmes outils, page par page (‹ Page 2 / 7 ›). Chaque page
// est rendue par pdf.js en image de 2 400 px ; elle garde ses formes, son
// historique et son repère (image → PDF) quand on passe à une autre. À la fin,
// les formes sont inscrites EN VECTORIEL dans une copie du PDF
// (utils/annotationPdf) : le plan reste net, son texte reste sélectionnable.
// Traits et textes y sont plus fins par défaut que sur une photo.

import { useEffect, useRef, useState, type PointerEvent as PointerEventReact } from 'react'
import { createPortal } from 'react-dom'
import type { PDFDocumentProxy } from 'pdfjs-dist'
import { ECRAN_PC } from '@/components/files/Visionneuse'
import { chargerPdfjs } from '@/components/files/chargerPdfjs'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { annoterPdf, inverser, PdfProtegeError, type Matrice } from '@/utils/annotationPdf'
import { ChevronLeft, ChevronRight, Loader2, Move, Send, Trash2, Undo2 } from 'lucide-react'
import {
  aimanter,
  boite,
  centre,
  deplacer,
  formeSous,
  poignees,
  redimensionner,
  tailleTexte,
  type Forme,
  type Point,
} from '@/utils/annotation'

type Outil = 'deplacer' | 'fleche' | 'cercle' | 'trait' | 'texte'

/** Zone de saisie d'une forme autour du doigt, en pixels d'écran. */
const TOLERANCE_ECRAN_PX = 22
/** Poignées de la forme choisie, en pixels d'écran. */
const RAYON_POIGNEE_PX = 11
const ECART_ROTATION_PX = 30
/** Pas des boutons A− / A+. */
const PAS_TAILLE = 1.2

const COTE_MAX = 2400

/**
 * Épaisseur des tracés, en pixels de l'image. Photo : 1/220 du grand côté
 * (retour du 09/10). PDF : 1/600, un plan se lit avec des traits fins
 * (≈ 1,4 pt sur un A4).
 */
function epaisseurPour(dims: { w: number; h: number }, pdf: boolean): number {
  const cote = Math.max(dims.w, dims.h)
  return pdf ? Math.max(2, Math.round(cote / 600)) : Math.max(3, Math.round(cote / 220))
}

/** Une page de PDF déjà ouverte dans l'éditeur. */
interface PageEditee {
  formes: Forme[]
  historique: Forme[][]
  /** Pixels de l'image de la page → espace du PDF. */
  matrice: Matrice
  dims: { w: number; h: number }
}

// Bleu par défaut (retour du 09/10) : se détache sur l'inox et le gris des
// machines, et ne se confond pas avec un défaut signalé en rouge.
const COULEURS: { nom: string; valeur: string }[] = [
  { nom: 'Bleu', valeur: '#0A6CFF' },
  { nom: 'Rouge', valeur: '#E60046' },
  { nom: 'Jaune', valeur: '#FFED00' },
  { nom: 'Blanc', valeur: '#FFFFFF' },
]

const OUTILS: { outil: Outil; libelle: string; icone: JSX.Element }[] = [
  { outil: 'deplacer', libelle: 'Déplacer', icone: <Move size={22} strokeWidth={2.2} aria-hidden="true" /> },
  {
    outil: 'fleche',
    libelle: 'Flèche',
    icone: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M5 19L19 5" /><path d="M9 5h10v10" />
      </svg>
    ),
  },
  {
    outil: 'cercle',
    libelle: 'Cercle',
    icone: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
        <circle cx="12" cy="12" r="8" />
      </svg>
    ),
  },
  {
    outil: 'trait',
    libelle: 'Trait',
    icone: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 17c3-6 5-9 7-9s2 6 4 6 3-4 7-8" />
      </svg>
    ),
  },
  {
    outil: 'texte',
    libelle: 'Texte',
    icone: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
        <path d="M5 6V4h14v2M12 4v16M9 20h6" />
      </svg>
    ),
  },
]

/** Charge une image depuis un Blob. Rejette si le navigateur ne sait pas la lire (HEIC sur PC). */
function chargerImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob)
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('image illisible'))
    }
    img.src = url
  })
}

/**
 * L'original d'abord (lu par Graph), puis les adresses de repli (aperçu) :
 * la première image lisible. Une photo HEIC sur PC ne se lit pas : l'aperçu
 * JPEG prend le relais.
 */
async function chargerDepuis(source: Exclude<SourceAnnotation, { pdf: unknown }>): Promise<HTMLImageElement> {
  if ('fichier' in source) return chargerImage(source.fichier)
  try {
    return await chargerImage(await source.lire())
  } catch {
    // Repli ci-dessous.
  }
  for (const adresse of source.adresses) {
    try {
      const r = await fetch(adresse)
      if (!r.ok) continue
      return await chargerImage(await r.blob())
    } catch {
      // Adresse expirée, format illisible : la suivante.
    }
  }
  throw new Error('aucune adresse lisible')
}

export type SourceAnnotation =
  | { fichier: Blob }
  | { lire: () => Promise<Blob>; adresses: string[] }
  /** Un PDF : son contenu (lu par Graph). */
  | { pdf: () => Promise<Blob> }

export function EditeurAnnotation({
  source,
  mode,
  enCours = false,
  libelleAnnuler = 'Annuler',
  libelleSansAnnotation,
  onAnnuler,
  onTerminer,
}: {
  source: SourceAnnotation
  mode: 'publier' | 'envoi'
  enCours?: boolean
  /** « Passer » quand l'éditeur s'ouvre de lui-même après une prise de photo. */
  libelleAnnuler?: string
  /**
   * Éditeur ouvert d'office après une prise de photo (12/10) : tant que rien n'est
   * dessiné, le bouton jaune reste actif sous ce libellé (« Ne pas annoter ») et
   * garde la photo telle quelle. « Passer », en haut à gauche, se trouvait mal.
   */
  libelleSansAnnotation?: string
  onAnnuler: () => void
  /** L'image annotée (JPEG) — ou le PDF annoté — et, en mode « publier », le commentaire saisi. */
  onTerminer: (image: Blob, commentaire: string) => void
}): JSX.Element {
  const estPdf = 'pdf' in source
  const toile = useRef<HTMLCanvasElement>(null)
  // La photo, ou l'image de la page de PDF affichée.
  const image = useRef<HTMLImageElement | HTMLCanvasElement | null>(null)
  // PDF : le document ouvert et ses octets d'origine (base du PDF annoté).
  const pdf = useRef<{ doc: PDFDocumentProxy; octets: Uint8Array } | null>(null)
  const [nbPages, setNbPages] = useState(1)
  const [page, setPage] = useState(0)
  // Les AUTRES pages déjà ouvertes : leurs formes et leur repère.
  const pagesEditees = useRef(new Map<number, PageEditee>())
  const matrice = useRef<Matrice | null>(null)
  // Dernier rendu de page demandé : un rendu plus lent ne l'écrase pas.
  const rendu = useRef(0)
  const [erreurExport, setErreurExport] = useState('')
  const [etat, setEtat] = useState<'chargement' | 'pret' | 'erreur'>('chargement')
  const [dims, setDims] = useState({ w: 1, h: 1 })
  const [formes, setFormes] = useState<Forme[]>([])
  // Chaque geste (tracé, déplacement, couleur, suppression) : on garde l'état
  // d'avant, pour « Annuler le dernier geste ».
  const [historique, setHistorique] = useState<Forme[][]>([])
  const [choisie, setChoisie] = useState<number | null>(null)
  const deplacement = useRef<{
    mode: 'deplacer' | 'taille' | 'rotation'
    i: number
    depart: Point
    origine: Forme
    centre: Point
    bouge: boolean
  } | null>(null)
  // Taille choisie pour les prochains textes (sinon : celle par défaut).
  const [tailleChoisie, setTailleChoisie] = useState<number | null>(null)
  const [brouillon, setBrouillon] = useState<Forme | null>(null)
  const [outil, setOutil] = useState<Outil>('fleche')
  const [couleur, setCouleur] = useState(COULEURS[0]!.valeur)
  const [texteAPlacer, setTexteAPlacer] = useState<Point | null>(null)
  const [texte, setTexte] = useState('')
  const [commentaire, setCommentaire] = useState('')
  const [export_, setExport] = useState(false)
  const pointeur = useRef<number | null>(null)
  const pc = useMediaQuery(ECRAN_PC)
  // Zone d'affichage : la photo y est mise à l'échelle, petite capture comprise.
  const zone = useRef<HTMLDivElement>(null)
  const [place, setPlace] = useState({ w: 0, h: 0 })
  useEffect(() => {
    const el = zone.current
    if (!el) return undefined
    const mesurer = (): void => setPlace({ w: el.clientWidth - 16, h: el.clientHeight })
    mesurer()
    const obs = new ResizeObserver(mesurer)
    obs.observe(el)
    return () => obs.disconnect()
  }, [])
  const echelle = place.w > 0 && place.h > 0 ? Math.min(place.w / dims.w, place.h / dims.h) : 0

  // Traits fins (retour du 09/10) : 1/220 du grand côté, environ 1,7 px à
  // l'écran d'un téléphone, 11 px sur une photo de 2 400 px.
  const epaisseur = epaisseurPour(dims, estPdf)

  /** Rend la page `i` du PDF en image (2 400 px sur le grand côté) et en garde le repère. */
  async function rendrePage(i: number): Promise<void> {
    const p = pdf.current
    if (!p) return
    const jeton = ++rendu.current
    setEtat('chargement')
    const pg = await p.doc.getPage(i + 1)
    const base = pg.getViewport({ scale: 1 })
    const viewport = pg.getViewport({ scale: COTE_MAX / Math.max(base.width, base.height) })
    const c = document.createElement('canvas')
    c.width = Math.round(viewport.width)
    c.height = Math.round(viewport.height)
    const ctx = c.getContext('2d')
    if (!ctx) throw new Error('canvas indisponible')
    await pg.render({ canvasContext: ctx, viewport }).promise
    if (jeton !== rendu.current) return
    matrice.current = inverser(viewport.transform as Matrice)
    image.current = c
    setDims({ w: c.width, h: c.height })
    setEtat('pret')
  }

  // Chargement de la photo, ou du PDF et de sa première page.
  useEffect(() => {
    let annule = false
    if ('pdf' in source) {
      ;(async () => {
        const [pdfjs, blob] = await Promise.all([chargerPdfjs(), source.pdf()])
        const octets = new Uint8Array(await blob.arrayBuffer())
        // pdf.js transfère à son worker les octets qu'on lui donne : une copie.
        const doc = await pdfjs.getDocument({ data: octets.slice() }).promise
        try {
          await document.fonts?.load(policeTexte(40))
        } catch {
          // Police indisponible : repli sur celle du système.
        }
        if (annule) {
          void doc.destroy()
          return
        }
        pdf.current = { doc, octets }
        setNbPages(doc.numPages)
        await rendrePage(0)
      })().catch(() => {
        if (!annule) setEtat('erreur')
      })
      return () => {
        annule = true
        void pdf.current?.doc.destroy()
      }
    }
    chargerDepuis(source)
      .then(async (img) => {
        // La police du texte doit être prête avant le premier dessin.
        try {
          await document.fonts?.load(policeTexte(40))
        } catch {
          // Police indisponible : repli sur celle du système.
        }
        if (annule) return
        const k = Math.min(1, COTE_MAX / Math.max(img.naturalWidth, img.naturalHeight))
        image.current = img
        setDims({ w: Math.round(img.naturalWidth * k), h: Math.round(img.naturalHeight * k) })
        setEtat('pret')
      })
      .catch(() => {
        if (!annule) setEtat('erreur')
      })
    return () => {
      annule = true
      const img = image.current
      if (img instanceof HTMLImageElement && img.src.startsWith('blob:')) URL.revokeObjectURL(img.src)
    }
    // La source ne change pas pendant la vie de l'éditeur.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /** PDF : passe à la page `i`, celle qu'on quitte garde ses formes et son historique. */
  function changerPage(i: number): void {
    if (i === page || i < 0 || i >= nbPages || !matrice.current) return
    pagesEditees.current.set(page, { formes, historique, matrice: matrice.current, dims })
    const suivante = pagesEditees.current.get(i)
    pagesEditees.current.delete(i)
    setFormes(suivante?.formes ?? [])
    setHistorique(suivante?.historique ?? [])
    setChoisie(null)
    setBrouillon(null)
    setTexteAPlacer(null)
    setTexte('')
    setPage(i)
    rendrePage(i).catch(() => setEtat('erreur'))
  }

  // Pages annotées (PDF), celle affichée comprise.
  const pagesAnnotees = [
    ...[...pagesEditees.current.entries()].filter(([, p]) => p.formes.length > 0).map(([i]) => i),
    ...(formes.length > 0 ? [page] : []),
  ].sort((a, b) => a - b)
  const rienDessine = pagesAnnotees.length === 0

  // Un PDF se lit à une autre échelle qu'une photo : texte plus petit par défaut
  // (1/50 du grand côté, ≈ 17 pt sur un A4).
  const tailleNeuve = tailleChoisie ?? (estPdf ? Math.round(Math.max(dims.w, dims.h) / 50) : tailleTexte(dims.w, dims.h, epaisseur))
  // Poignées : taille constante à l'écran, quelle que soit la définition de la photo.
  const parPixelEcran = 1 / (echelle || 1)
  const rayonPoignee = RAYON_POIGNEE_PX * parPixelEcran
  const marge = epaisseur * 2
  const ecartRotation = ECART_ROTATION_PX * parPixelEcran

  /** Largeur d'un texte posé, mesurée avec la police du dessin. */
  function largeurTexte(f: Forme): number {
    const ctx = toile.current?.getContext('2d')
    if (!ctx || f.outil !== 'texte') return 0
    ctx.save()
    ctx.font = policeTexte(f.taille)
    const w = ctx.measureText(f.texte).width
    ctx.restore()
    return w
  }

  // Dessin : la photo, puis chaque forme, et le cadre de la forme choisie.
  useEffect(() => {
    const ctx = toile.current?.getContext('2d')
    const img = image.current
    if (!ctx || !img || etat !== 'pret') return
    ctx.drawImage(img, 0, 0, dims.w, dims.h)
    for (const f of brouillon ? [...formes, brouillon] : formes) dessiner(ctx, f, epaisseur, largeurTexte(f))
    const f = outil === 'deplacer' && choisie !== null ? formes[choisie] : undefined
    if (f) dessinerSelection(ctx, f, largeurTexte(f), marge, rayonPoignee, ecartRotation)
    // largeurTexte ne dépend que de la toile et des formes, déjà suivies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formes, brouillon, dims, etat, epaisseur, outil, choisie, rayonPoignee, ecartRotation, marge])

  function changer(suivantes: Forme[]): void {
    setHistorique((h) => [...h, formes])
    setFormes(suivantes)
  }

  function annulerGeste(): void {
    const precedent = historique[historique.length - 1]
    if (!precedent) return
    setHistorique((h) => h.slice(0, -1))
    setFormes(precedent)
    setChoisie(null)
  }

  function choisirCouleur(valeur: string): void {
    setCouleur(valeur)
    // Une forme choisie prend la couleur touchée.
    const f = outil === 'deplacer' && choisie !== null ? formes[choisie] : undefined
    if (f && f.couleur !== valeur) changer(formes.map((g, i) => (i === choisie ? { ...g, couleur: valeur } : g)))
  }

  /** A− / A+ : la forme choisie, ou la taille des prochains textes. */
  function changerTaille(k: number): void {
    const f = outil === 'deplacer' && choisie !== null ? formes[choisie] : undefined
    if (f) {
      changer(formes.map((g, i) => (i === choisie ? redimensionner(g, k, centre(g, largeurTexte(g))) : g)))
      return
    }
    setTailleChoisie(Math.max(8, Math.round(tailleNeuve * k)))
  }

  function supprimerChoisie(): void {
    if (choisie === null) return
    changer(formes.filter((_, i) => i !== choisie))
    setChoisie(null)
  }

  function versToile(e: PointerEventReact<HTMLCanvasElement>): Point {
    const r = e.currentTarget.getBoundingClientRect()
    return { x: ((e.clientX - r.left) * dims.w) / r.width, y: ((e.clientY - r.top) * dims.h) / r.height }
  }

  function onPointerDown(e: PointerEventReact<HTMLCanvasElement>): void {
    if (pointeur.current !== null || enCours) return
    const p = versToile(e)
    if (outil === 'deplacer') {
      // Une poignée de la forme déjà choisie d'abord : agrandir, pivoter.
      const actuelle = choisie !== null ? formes[choisie] : undefined
      if (actuelle && choisie !== null) {
        const q = poignees(actuelle, largeurTexte(actuelle), marge, ecartRotation)
        const saisie = rayonPoignee * 1.8
        const mode = Math.hypot(p.x - q.taille.x, p.y - q.taille.y) <= saisie
          ? 'taille'
          : Math.hypot(p.x - q.rotation.x, p.y - q.rotation.y) <= saisie
            ? 'rotation'
            : null
        if (mode) {
          pointeur.current = e.pointerId
          e.currentTarget.setPointerCapture(e.pointerId)
          deplacement.current = { mode, i: choisie, depart: p, origine: actuelle, centre: q.centre, bouge: false }
          return
        }
      }
      const tolerance = TOLERANCE_ECRAN_PX * parPixelEcran
      const i = formeSous(formes, p, tolerance, largeurTexte)
      setChoisie(i >= 0 ? i : null)
      if (i < 0) return
      pointeur.current = e.pointerId
      e.currentTarget.setPointerCapture(e.pointerId)
      const f = formes[i]!
      deplacement.current = { mode: 'deplacer', i, depart: p, origine: f, centre: centre(f, largeurTexte(f)), bouge: false }
      return
    }
    if (outil === 'texte') {
      setTexteAPlacer(p)
      return
    }
    pointeur.current = e.pointerId
    e.currentTarget.setPointerCapture(e.pointerId)
    setBrouillon({ outil, couleur, points: [p] })
  }

  function onPointerMove(e: PointerEventReact<HTMLCanvasElement>): void {
    if (e.pointerId !== pointeur.current) return
    const d = deplacement.current
    if (d) {
      const p = versToile(e)
      // Premier mouvement : l'état d'avant entre dans l'historique, une fois.
      if (!d.bouge) {
        d.bouge = true
        setHistorique((h) => [...h, formes])
      }
      let modifiee: Forme
      if (d.mode === 'taille') {
        // Écart au centre, relatif à celui du départ : on tire ou on pousse le coin.
        const k = Math.hypot(p.x - d.centre.x, p.y - d.centre.y) /
          Math.max(1, Math.hypot(d.depart.x - d.centre.x, d.depart.y - d.centre.y))
        modifiee = redimensionner(d.origine, Math.min(20, Math.max(0.1, k)), d.centre)
      } else if (d.mode === 'rotation') {
        const tour = Math.atan2(p.y - d.centre.y, p.x - d.centre.x) -
          Math.atan2(d.depart.y - d.centre.y, d.depart.x - d.centre.x)
        modifiee = { ...d.origine, angle: aimanter((d.origine.angle ?? 0) + tour) }
      } else {
        modifiee = deplacer(d.origine, p.x - d.depart.x, p.y - d.depart.y)
      }
      setFormes((prev) => prev.map((f, i) => (i === d.i ? modifiee : f)))
      return
    }
    if (!brouillon || brouillon.outil === 'texte') return
    const p = versToile(e)
    setBrouillon({
      ...brouillon,
      points: brouillon.outil === 'trait' ? [...brouillon.points, p] : [brouillon.points[0]!, p],
    })
  }

  function onPointerUp(e: PointerEventReact<HTMLCanvasElement>): void {
    if (e.pointerId !== pointeur.current) return
    pointeur.current = null
    if (deplacement.current) {
      deplacement.current = null
      return
    }
    const f = brouillon
    setBrouillon(null)
    if (!f || f.points.length < 2) return
    const [a, b] = [f.points[0]!, f.points[f.points.length - 1]!]
    // Un simple toucher ne laisse pas de trace.
    if (f.outil !== 'trait' && Math.hypot(a.x - b.x, a.y - b.y) < epaisseur * 2) return
    changer([...formes, f])
  }

  function placerTexte(): void {
    const t = texte.trim()
    if (t && texteAPlacer) changer([...formes, { outil: 'texte', couleur, points: [texteAPlacer], texte: t, taille: tailleNeuve }])
    setTexte('')
    setTexteAPlacer(null)
  }

  const sansAnnotation = Boolean(libelleSansAnnotation) && formes.length === 0

  function terminer(): void {
    // Rien dessiné et sortie proposée : la photo part telle quelle.
    if (sansAnnotation) {
      onAnnuler()
      return
    }
    if (estPdf) {
      void exporterPdf()
      return
    }
    const t = toile.current
    if (!t || formes.length === 0) return
    // Le cadre de sélection ne doit pas partir dans la photo.
    const ctx = t.getContext('2d')
    if (ctx && image.current) {
      ctx.drawImage(image.current, 0, 0, dims.w, dims.h)
      for (const f of formes) dessiner(ctx, f, epaisseur, largeurTexte(f))
    }
    setChoisie(null)
    setExport(true)
    t.toBlob(
      (blob) => {
        setExport(false)
        if (blob) onTerminer(blob, commentaire.trim())
      },
      'image/jpeg',
      0.9,
    )
  }

  /** PDF : les formes de toutes les pages, inscrites en vectoriel dans une copie. */
  async function exporterPdf(): Promise<void> {
    const p = pdf.current
    if (!p || !matrice.current) return
    const toutes = new Map(pagesEditees.current)
    toutes.set(page, { formes, historique, matrice: matrice.current, dims })
    const annotees = [...toutes.entries()]
      .filter(([, v]) => v.formes.length > 0)
      .map(([index, v]) => ({ index, formes: v.formes, matrice: v.matrice, epaisseur: epaisseurPour(v.dims, true) }))
    if (annotees.length === 0) return
    setChoisie(null)
    setErreurExport('')
    setExport(true)
    try {
      // TT Norms Pro, comme à l'écran (la graisse Regular : seule disponible en TTF).
      const police = await fetch('/fonts/TT_Norms_Pro_Regular.ttf')
        .then((r) => (r.ok ? r.arrayBuffer() : undefined))
        .catch(() => undefined)
      const octets = await annoterPdf(p.octets, annotees, police)
      onTerminer(new Blob([new Uint8Array(octets)], { type: 'application/pdf' }), commentaire.trim())
    } catch (e) {
      setErreurExport(e instanceof PdfProtegeError ? e.message : 'Le PDF annoté n’a pas pu être préparé. Réessayez.')
    } finally {
      setExport(false)
    }
  }

  const occupe = enCours || export_
  const blanc = 'rgba(255,255,255,0.65)'

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={estPdf ? 'Annoter le PDF' : 'Annoter la photo'}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 90,
        background: '#111',
        color: 'white',
        display: 'flex',
        flexDirection: pc ? 'row' : 'column',
      }}
    >
      {/* Colonne de la photo : en-tête et toile */}
      <div className="flex flex-col" style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
      <div
        className="flex items-center justify-between"
        style={{ flexShrink: 0, padding: 'calc(8px + env(safe-area-inset-top, 0px)) 12px 8px' }}
      >
        <button
          type="button"
          onClick={onAnnuler}
          disabled={occupe}
          style={{ minHeight: 44, padding: '0 6px', background: 'none', border: 'none', color: 'white', fontSize: 16 }}
        >
          {libelleAnnuler}
        </button>
        {estPdf && nbPages > 1 ? (
          // PDF : ‹ Page 2 / 7 ›, au-dessus de la page. Un point jaune : page annotée.
          <div className="flex items-center" style={{ gap: 2 }}>
            <button
              type="button"
              onClick={() => changerPage(page - 1)}
              disabled={page === 0 || occupe || etat === 'chargement'}
              aria-label="Page précédente"
              className="flex items-center justify-center"
              style={{ width: 40, height: 40, background: 'none', border: 'none', color: 'white', opacity: page === 0 ? 0.3 : 1 }}
            >
              <ChevronLeft size={22} />
            </button>
            <span className="flex items-center" style={{ gap: 6, fontSize: 16, fontWeight: 700, minWidth: 104, justifyContent: 'center', whiteSpace: 'nowrap' }}>
              Page {page + 1} / {nbPages}
              {formes.length > 0 && (
                <span aria-label="annotée" style={{ width: 8, height: 8, borderRadius: 4, background: 'var(--color-yellow)' }} />
              )}
            </span>
            <button
              type="button"
              onClick={() => changerPage(page + 1)}
              disabled={page === nbPages - 1 || occupe || etat === 'chargement'}
              aria-label="Page suivante"
              className="flex items-center justify-center"
              style={{ width: 40, height: 40, background: 'none', border: 'none', color: 'white', opacity: page === nbPages - 1 ? 0.3 : 1 }}
            >
              <ChevronRight size={22} />
            </button>
          </div>
        ) : (
          <span style={{ fontSize: 17, fontWeight: 700 }}>Annoter</span>
        )}
        <button
          type="button"
          onClick={annulerGeste}
          disabled={historique.length === 0 || occupe}
          aria-label="Annuler le dernier geste"
          className="flex items-center justify-center"
          style={{
            width: 44,
            height: 44,
            background: 'none',
            border: 'none',
            color: 'white',
            opacity: historique.length === 0 ? 0.35 : 1,
          }}
        >
          <Undo2 size={22} />
        </button>
      </div>

      <div ref={zone} className="flex items-center justify-center" style={{ flex: 1, minHeight: 0, padding: '0 8px' }}>
        {etat === 'chargement' && (
          <Loader2 size={28} className="animate-spin" aria-label={estPdf ? 'Chargement de la page' : 'Chargement de la photo'} />
        )}
        {etat === 'erreur' && (
          <p style={{ textAlign: 'center', color: blanc, fontSize: 15, padding: 24 }}>
            {estPdf ? 'Ce PDF ne peut pas être ouvert ici.' : 'Cette photo ne peut pas être annotée sur cet appareil.'}
          </p>
        )}
        <canvas
          ref={toile}
          width={dims.w}
          height={dims.h}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          aria-label={outil === 'deplacer'
            ? 'Photo annotée : touchez une forme pour la déplacer ; ses poignées l’agrandissent ou la font pivoter'
            : 'Photo à annoter : glissez pour dessiner'}
          style={{
            display: etat === 'pret' ? 'block' : 'none',
            ...(echelle > 0
              ? { width: Math.floor(dims.w * echelle), height: Math.floor(dims.h * echelle) }
              : { maxWidth: '100%', maxHeight: '100%' }),
            touchAction: 'none',
            cursor: outil === 'texte' ? 'text' : outil === 'deplacer' ? 'move' : 'crosshair',
          }}
        />
      </div>

      </div>

      <div
        className="flex flex-col"
        style={pc
          ? {
              width: 340,
              flexShrink: 0,
              gap: 14,
              background: '#000',
              padding: '20px 20px 24px',
              overflowY: 'auto',
              boxSizing: 'border-box',
              borderLeft: '1px solid rgba(255,255,255,0.1)',
            }
          : {
              flexShrink: 0,
              gap: 12,
              background: '#000',
              padding: '10px 16px calc(18px + env(safe-area-inset-bottom, 0px))',
              width: '100%',
              maxWidth: 720,
              marginInline: 'auto',
              boxSizing: 'border-box',
            }}
      >
        {/* Outils sur une ligne, couleurs (et suppression) sur la suivante :
            tout tient sur un téléphone de 375 px. */}
        <div role="toolbar" aria-label="Outils" className="flex items-center" style={{ gap: 2 }}>
          {OUTILS.map((o) => {
            const actif = o.outil === outil
            return (
              <button
                key={o.outil}
                type="button"
                aria-pressed={actif}
                onClick={() => {
                  setOutil(o.outil)
                  if (o.outil !== 'deplacer') setChoisie(null)
                  // Un texte en attente n'a plus de sens avec un autre outil.
                  if (o.outil !== 'texte') {
                    setTexteAPlacer(null)
                    setTexte('')
                  }
                }}
                className="flex flex-col items-center justify-center"
                style={{
                  flex: '1 1 0',
                  minWidth: 0,
                  height: 54,
                  gap: 3,
                  borderRadius: 12,
                  border: 'none',
                  background: actif ? 'white' : 'none',
                  color: actif ? 'black' : 'white',
                  fontSize: 11,
                  padding: 0,
                }}
              >
                {o.icone}
                {o.libelle}
              </button>
            )
          })}
        </div>
        <div className="flex items-center" style={{ gap: 10, minHeight: 36 }}>
          <div role="group" aria-label="Couleur" className="flex" style={{ gap: 8 }}>
            {COULEURS.map((c) => (
              <button
                key={c.valeur}
                type="button"
                aria-label={c.nom}
                aria-pressed={c.valeur === couleur}
                onClick={() => choisirCouleur(c.valeur)}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 15,
                  padding: 0,
                  background: c.valeur,
                  border: c.valeur === couleur ? '3px solid white' : '2px solid rgba(255,255,255,0.35)',
                  boxShadow: c.valeur === couleur ? '0 0 0 2px #000 inset' : 'none',
                }}
              />
            ))}
          </div>
          {(outil === 'texte' || (outil === 'deplacer' && choisie !== null)) && (
            <div role="group" aria-label="Taille" className="flex" style={{ gap: 4 }}>
              {[
                { libelle: 'A−', nom: 'Plus petit', k: 1 / PAS_TAILLE, taillePolice: 13 },
                { libelle: 'A+', nom: 'Plus grand', k: PAS_TAILLE, taillePolice: 17 },
              ].map((b) => (
                <button
                  key={b.libelle}
                  type="button"
                  aria-label={b.nom}
                  onClick={() => changerTaille(b.k)}
                  style={{
                    width: 36,
                    height: 32,
                    borderRadius: 8,
                    border: 'none',
                    background: 'rgba(255,255,255,0.14)',
                    color: 'white',
                    fontSize: b.taillePolice,
                    fontWeight: 500,
                    padding: 0,
                  }}
                >
                  {b.libelle}
                </button>
              ))}
            </div>
          )}
          <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: blanc, textAlign: 'right' }}>
            {outil === 'deplacer' && choisie === null && (pc ? 'Cliquez une forme pour la déplacer.' : 'Touchez une forme pour la déplacer.')}
            {outil === 'texte' && !texteAPlacer && (pc ? 'Cliquez la photo où placer le texte.' : 'Touchez la photo où placer le texte.')}
          </span>
          {outil === 'deplacer' && choisie !== null && (
            <button
              type="button"
              onClick={supprimerChoisie}
              aria-label="Supprimer la forme"
              title="Supprimer la forme"
              className="flex items-center justify-center"
              style={{
                width: 40,
                height: 34,
                borderRadius: 17,
                border: 'none',
                background: 'rgba(255,255,255,0.14)',
                color: 'white',
                flexShrink: 0,
              }}
            >
              <Trash2 size={17} />
            </button>
          )}
        </div>

        {/* Pas de <form> : il remonterait jusqu'au formulaire de la page. */}
        {texteAPlacer && (
          <div className="flex" style={{ gap: 8 }}>
            <input
              autoFocus
              value={texte}
              onChange={(e) => setTexte(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return
                e.preventDefault()
                placerTexte()
              }}
              enterKeyHint="done"
              placeholder="Texte à placer sur la photo"
              aria-label="Texte à placer sur la photo"
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: 16,
                color: 'white',
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.25)',
                borderRadius: 10,
                padding: '10px 12px',
              }}
            />
            <button
              type="button"
              onClick={placerTexte}
              style={{ padding: '0 16px', borderRadius: 10, border: 'none', background: 'white', color: 'black', fontSize: 15, fontWeight: 500 }}
            >
              Placer
            </button>
          </div>
        )}

        {mode === 'publier' && (
          <label className="flex flex-col" style={{ gap: 6 }}>
            <span style={{ fontSize: 13, color: blanc }}>Commentaire (facultatif)</span>
            <textarea
              rows={2}
              value={commentaire}
              onChange={(e) => setCommentaire(e.target.value)}
              style={{
                fontSize: 16,
                lineHeight: '22px',
                color: 'white',
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.25)',
                borderRadius: 10,
                padding: '10px 12px',
                resize: 'none',
              }}
            />
          </label>
        )}

        {erreurExport && (
          <p role="alert" style={{ margin: 0, fontSize: 14, color: 'white', background: 'var(--color-traffic-red)', borderRadius: 8, padding: '8px 10px' }}>
            {erreurExport}
          </p>
        )}

        <button
          type="button"
          onClick={terminer}
          disabled={(rienDessine && !sansAnnotation) || occupe || (etat !== 'pret' && !sansAnnotation)}
          className="flex items-center justify-center"
          style={{
            // PC : en bas du panneau.
            ...(pc ? { marginTop: 'auto' } : {}),
            flexShrink: 0,
            height: 52,
            gap: 8,
            borderRadius: 10,
            border: 'none',
            background: 'var(--color-yellow)',
            color: 'black',
            fontSize: 17,
            fontWeight: 700,
            opacity: (rienDessine && !sansAnnotation) || occupe ? 0.5 : 1,
          }}
        >
          {occupe ? <Loader2 size={19} className="animate-spin" /> : mode === 'publier' ? <Send size={19} /> : null}
          {sansAnnotation ? libelleSansAnnotation : mode === 'publier' ? 'Publier dans l’activité' : 'Terminé'}
        </button>
        <span style={{ fontSize: 13, textAlign: 'center', color: blanc }}>
          {estPdf
            ? pagesAnnotees.length > 0
              ? `${pagesAnnotees.length > 1 ? 'Pages annotées' : 'Page annotée'} : ${pagesAnnotees.map((i) => i + 1).join(', ')}. Le PDF d’origine est conservé.`
              : 'Le PDF d’origine est conservé.'
            : 'La photo d’origine est conservée.'}
        </span>
      </div>
    </div>,
    document.body,
  )
}

// ─── Tracés ───

/** TT Norms Pro Medium : la police de l'application, plus fine que du gras. */
function policeTexte(taille: number): string {
  return `500 ${taille}px 'TT Norms Pro', system-ui, -apple-system, sans-serif`
}

/** Applique l'orientation de la forme autour du centre de son cadre. */
function orienter(ctx: CanvasRenderingContext2D, f: Forme, largeurTexte: number): void {
  if (!f.angle) return
  const c = centre(f, largeurTexte)
  ctx.translate(c.x, c.y)
  ctx.rotate(f.angle)
  ctx.translate(-c.x, -c.y)
}

/** Forme choisie (outil Déplacer) : cadre pointillé, poignées agrandir et pivoter. */
function dessinerSelection(
  ctx: CanvasRenderingContext2D,
  f: Forme,
  largeurTexte: number,
  marge: number,
  rayon: number,
  ecartRotation: number,
): void {
  const b = boite(f, largeurTexte)
  const q = poignees(f, largeurTexte, marge, ecartRotation)
  ctx.save()
  ctx.shadowColor = 'rgba(0,0,0,0.7)'
  ctx.shadowBlur = rayon / 2
  ctx.strokeStyle = 'white'
  ctx.lineWidth = Math.max(2, rayon / 5)

  ctx.save()
  orienter(ctx, f, largeurTexte)
  ctx.setLineDash([rayon, rayon * 0.7])
  ctx.strokeRect(b.x - marge, b.y - marge, b.w + marge * 2, b.h + marge * 2)
  ctx.restore()

  // Tige de la poignée de rotation.
  ctx.beginPath()
  ctx.moveTo(q.attache.x, q.attache.y)
  ctx.lineTo(q.rotation.x, q.rotation.y)
  ctx.stroke()

  for (const [p, plein] of [[q.taille, true], [q.rotation, false]] as const) {
    ctx.beginPath()
    ctx.arc(p.x, p.y, rayon, 0, Math.PI * 2)
    ctx.fillStyle = plein ? 'white' : '#0A6CFF'
    ctx.fill()
    ctx.stroke()
  }
  // Repère de la poignée de rotation : une flèche courbe.
  ctx.beginPath()
  ctx.strokeStyle = 'white'
  ctx.lineWidth = Math.max(1.5, rayon / 6)
  ctx.arc(q.rotation.x, q.rotation.y, rayon * 0.5, -Math.PI * 0.9, Math.PI * 0.4)
  ctx.stroke()
  ctx.restore()
}

function dessiner(ctx: CanvasRenderingContext2D, f: Forme, ep: number, largeurTexte: number): void {
  ctx.save()
  orienter(ctx, f, largeurTexte)
  ctx.strokeStyle = f.couleur
  ctx.fillStyle = f.couleur
  ctx.lineWidth = ep
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  // Une ombre légère : le tracé reste lisible sur fond clair comme sombre.
  ctx.shadowColor = 'rgba(0,0,0,0.45)'
  ctx.shadowBlur = ep * 0.8

  const [a, b] = [f.points[0]!, f.points[f.points.length - 1]!]
  if (f.outil === 'trait') {
    ctx.beginPath()
    f.points.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)))
    ctx.stroke()
  } else if (f.outil === 'cercle') {
    ctx.beginPath()
    ctx.ellipse((a.x + b.x) / 2, (a.y + b.y) / 2, Math.abs(b.x - a.x) / 2, Math.abs(b.y - a.y) / 2, 0, 0, Math.PI * 2)
    ctx.stroke()
  } else if (f.outil === 'fleche') {
    const angle = Math.atan2(b.y - a.y, b.x - a.x)
    // Pointe un peu plus longue : elle reste visible malgré le trait fin.
    const pointe = ep * 6
    ctx.beginPath()
    ctx.moveTo(a.x, a.y)
    ctx.lineTo(b.x, b.y)
    ctx.moveTo(b.x - pointe * Math.cos(angle - 0.5), b.y - pointe * Math.sin(angle - 0.5))
    ctx.lineTo(b.x, b.y)
    ctx.lineTo(b.x - pointe * Math.cos(angle + 0.5), b.y - pointe * Math.sin(angle + 0.5))
    ctx.stroke()
  } else if (f.outil === 'texte') {
    // Sans contour (retour du 09/10) ; seule une ombre douce le détache
    // d'un fond de la même couleur.
    ctx.font = policeTexte(f.taille)
    ctx.textBaseline = 'middle'
    ctx.shadowColor = 'rgba(0,0,0,0.5)'
    ctx.shadowBlur = f.taille * 0.15
    ctx.fillText(f.texte, a.x, a.y)
  }
  ctx.restore()
}
